import React from "react";
import { BookOpen, Building2, CalendarDays, ClipboardList, FileText, GraduationCap, LayoutDashboard, ListChecks, Settings2, Table2, UserCog, Users } from "lucide-react";
import { ACPPProvider, useACPP } from "./ACPPContext";
import ACPPPageHeader from "./components/layout/ACPPPageHeader";
import Module1SchoolMaster from "./modules/Module1SchoolMaster";
import Module2SetupLogic from "./modules/Module2SetupLogic";
import Module3GradesOffered from "./modules/Module3GradesOffered";
import Module4AcademicPlanning from "./modules/Module4AcademicPlanning";
import Module5OwnershipMapping from "./modules/Module5OwnershipMapping";
import Module6TeacherWorkflow from "./modules/Module6TeacherWorkflow";
import Module7AssessmentStructure from "./modules/Module7AssessmentStructure";
import Module8TeacherDevelopment from "./modules/Module8TeacherDevelopment";
import Module9StudentDevelopment from "./modules/Module9StudentDevelopment";
import Module10AcademicCalendar from "./modules/Module10AcademicCalendar";
import Module11DynamicTimetable from "./modules/Module11DynamicTimetable";
import Module12LeadershipDashboard from "./modules/Module12LeadershipDashboard";
import Module13LessonPlanOutline from "./modules/Module13LessonPlanOutline";

const MODULE_COMPONENTS = {
  module1: Module1SchoolMaster,
  module2: Module2SetupLogic,
  module3: Module3GradesOffered,
  module4: Module4AcademicPlanning,
  module5: Module5OwnershipMapping,
  module6: Module6TeacherWorkflow,
  module7: Module7AssessmentStructure,
  module8: Module8TeacherDevelopment,
  module9: Module9StudentDevelopment,
  module10: Module10AcademicCalendar,
  module11: Module11DynamicTimetable,
  module12: Module12LeadershipDashboard,
  module13: Module13LessonPlanOutline,
};

const PAGE_META = {
  module1: { icon: Building2, title: "School Master", subtitle: "Your school's identity and basic details" },
  module2: { icon: Settings2, title: "Setup Logic", subtitle: "How annual planning is organised for your school" },
  module3: { icon: GraduationCap, title: "Grades Offered", subtitle: "Choose the grades your school teaches" },
  module4: { icon: BookOpen, title: "Academic Planning", subtitle: "Subjects offered for each grade" },
  module5: { icon: Users, title: "Ownership Mapping", subtitle: "Who teaches and who reviews each class and subject" },
  module6: { icon: ClipboardList, title: "Teacher Workflow", subtitle: "Every class's academic plan" },
  module7: { icon: FileText, title: "ACPP Assessment Outline", subtitle: "Assessment structure for each class and term" },
  module8: { icon: UserCog, title: "Teacher Development", subtitle: "Development and training plans for staff" },
  module9: { icon: ListChecks, title: "Student Development", subtitle: "Development plans for students" },
  module10: { icon: CalendarDays, title: "Academic Calendar", subtitle: "Terms, events and key dates for the year" },
  module11: { icon: Table2, title: "Dynamic Timetable", subtitle: "Section and teacher timetables" },
  module12: { icon: LayoutDashboard, title: "Leadership Dashboard", subtitle: "Planning health at a glance" },
  module13: { icon: BookOpen, title: "ACPP Lesson Plan Outline", subtitle: "Chapter-wise lesson outlines for every class" },
};

function ACPPModulePageInner({ moduleKey = "module1" }) {
  const ActiveModule = MODULE_COMPONENTS[moduleKey] || Module1SchoolMaster;
  const meta = PAGE_META[moduleKey] || PAGE_META.module1;
  const { getActiveAcademicYear } = useACPP();
  const year = getActiveAcademicYear();

  return (
    <>
      <div className="min-h-screen bg-white pt-16 font-[poppins]">
        <div className="w-full px-4 pb-12 pt-6 sm:px-6 lg:px-8">
          <ACPPPageHeader icon={meta.icon} title={meta.title} subtitle={year ? `${meta.subtitle} • Academic year ${year}` : meta.subtitle} />
          <section className="min-w-0">
            <ActiveModule />
          </section>
        </div>
      </div>
    </>
  );
}

export default function ACPPModulePage({ moduleKey }) {
  return (
    <ACPPProvider>
      <ACPPModulePageInner moduleKey={moduleKey} />
    </ACPPProvider>
  );
}
